import type { RawParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { router } from "single-spa/react-app/router/router";
import categoryService from "app/common/services/categoryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

const getParams = (): RawParams => {
    const nextParams = router?.globals?.transition?.params?.();
    const currentParams = proNavigator.getParams();

    return nextParams ?? currentParams;
};

type Predicate = (params: RawParams) => boolean;

const predicates = {
    hasSegments: (params) => {
        const { category } = params;
        const categoryParams = Array.isArray(category) ? category : [category];
        return categoryParams.some((categoryParam) => {
            const categoryObject = categoryService.categoryQueryParamToCategoryObject(categoryParam);
            return categoryObject?.segments?.length > 0;
        });
    },
    isWorldwide: (params) => {
        const { country } = params;
        return country && country === String(WORLDWIDE_COUNTRY_ID);
    },
    isDesktop: (params) => {
        const { webSource } = params;
        return webSource && webSource === devicesTypes.DESKTOP;
    },
    isTotal: (params) => {
        const { webSource } = params;
        return webSource && webSource === devicesTypes.TOTAL;
    },
    isMobileWeb: (params) => {
        const { webSource } = params;
        return webSource && webSource === devicesTypes.MOBILE;
    },
} satisfies Record<string, Predicate>;

type FlagKey = keyof typeof predicates;

const resolveConfigId = (resolver: (flags: Record<FlagKey, boolean>) => string) => {
    const params = getParams();
    const flags = Object.fromEntries(
        Object.entries(predicates).map(([key, predicate]) => [key, predicate(params)]),
    ) as Record<FlagKey, boolean>;
    return resolver(flags);
};

export const resolveConfigIdWorldwideWrapper = (defaultConfigId: string, industryWithSegmentConfigId: string) => {
    return resolveConfigId(({ hasSegments, isWorldwide }) =>
        isWorldwide && hasSegments ? industryWithSegmentConfigId : defaultConfigId,
    );
};

export const resolveConfigIdForWebSource = ({
    industryWithSegmentConfigId = "",
    mobileWebConfigId,
    desktopConfigId,
}) => {
    return resolveConfigId(({ hasSegments, isTotal, isMobileWeb }) => {
        switch (true) {
            case hasSegments:
                return industryWithSegmentConfigId;
            case isTotal || isMobileWeb:
                return mobileWebConfigId;
            default:
                return desktopConfigId;
        }
    });
};
